import { api } from '@/api'
import { user } from '@/store'

/**
 * 分享商品落地。调用时用户已经登录并选过企业。
 *
 * 未登录：先去登录，选完企业后再回到这个商品，然后走这里。
 * 已在该订货会：不调用这里，直接看商品。
 * 已登录但订货会不同：切到商品这场；门店还能用或只有一个门店就自动选上，否则去选门店后再回来。
 * 进不了这场：不改当前订货会和门店，返回提示文案。
 */
export type ShareEntryResult =
  | { type: 'ready' }
  | { type: 'pickShop' }
  | { type: 'blocked'; message: string }

function fairBlockMessage(row: any, forGoods: boolean) {
  const role = (user.userData as { role?: number } | null)?.role
  const action = forGoods ? '无法查看此商品' : '无法进入'
  if (!row) return `您未参加该订货会，${action}`
  if (row.fair_status === 2) return `该订货会已结束，${action}`
  if (row.fair_status === 0 && role != 1) return `该订货会尚未开始，${action}`
  if (row.fair_status === 1 && row.fair_customers_company?.is_allow_order == 0) {
    return '该订货会暂不对您开放，请等待运营通知'
  }
  return ''
}

function restoreSession(meeting: number | null, shop: number | null) {
  if (meeting) user.setSelectMeeting(meeting)
  else user.setSelectMeeting()
  if (shop) user.setSelectShop(shop)
  else user.setSelectShop()
}

export async function enterSharedFair(fairId: number, forGoods = false): Promise<ShareEntryResult> {
  const prevMeeting = user.selectMeeting
  const prevShop = user.selectShop

  const listRes = await api.user.selectMeetingList()
  if (listRes.error) {
    return {
      type: 'blocked',
      message: listRes.error.message || '暂时无法进入该订货会',
    }
  }

  const fair = (listRes.data || []).find((item) => Number(item.id) === fairId)
  const blocked = fairBlockMessage(fair, forGoods)
  if (blocked) return { type: 'blocked', message: blocked }

  const meetingRes = await api.user.selectMeeting({ fair_id: fairId })
  if (meetingRes.error) {
    restoreSession(prevMeeting, prevShop)
    return {
      type: 'blocked',
      message: meetingRes.error.message || '暂时无法进入该订货会',
    }
  }

  const shopRes = await api.user.selectShopList({ fair_id: fairId })
  if (shopRes.error) {
    restoreSession(prevMeeting, prevShop)
    return {
      type: 'blocked',
      message: shopRes.error.message || '暂时无法进入该订货会',
    }
  }

  const shops = shopRes.data || []
  const currentShop = shops.find((item) => Number(item.id) === Number(prevShop))
  const targetShop = currentShop || (shops.length === 1 ? shops[0] : null)
  if (!targetShop) {
    user.setSelectMeeting(fairId)
    user.setSelectShop()
    return { type: 'pickShop' }
  }

  const selected = await api.user.selectShop({
    shop_id: targetShop.id,
    fair_id: fairId,
  })
  if (selected.error) {
    restoreSession(prevMeeting, prevShop)
    return {
      type: 'blocked',
      message: selected.error.message || '暂时无法进入该订货会',
    }
  }

  user.setSelectMeeting(fairId)
  user.setSelectShop(targetShop.id)

  const fairInfo = await api.user.getFairInfo({
    fair_id: fairId,
    shop_id: targetShop.id,
  })
  if (!fairInfo.error && fairInfo.data) {
    user.setFairData(fairInfo.data)
  }
  return { type: 'ready' }
}

export function enterSharedGoods(fairId: number) {
  return enterSharedFair(fairId, true)
}
